import { useEffect, useRef } from "react";
import { Play } from "lucide-react";

/**
 * Slim wrapper around <audio controls> that saves & resumes playback position
 * to localStorage (keyed by storageKey). Used for:
 *   - Storefront: 60-sec audio preview (no resume needed — pass storageKey={null})
 *   - Library:    full-length streaming with resume-where-you-left-off
 *
 * Optional props:
 *   - initialSeek (number): seconds to start playback from (server-provided; wins over localStorage if set)
 *   - onTimeUpdate (fn):    called with the underlying <audio> element on timeupdate — used to sync
 *                           position to the server without duplicating logic here
 */
export default function AudioPlayer({ src, storageKey = null, label, testid, initialSeek = null, onTimeUpdate = null }) {
  const audioRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => {
      try {
        if (initialSeek != null && initialSeek > 3 && initialSeek < (a.duration || 0) - 3) {
          a.currentTime = initialSeek;
          return;
        }
        if (storageKey) {
          const t = parseFloat(localStorage.getItem(storageKey) || "0");
          if (t > 3 && t < (a.duration || 0) - 3) a.currentTime = t;
        }
      } catch { /* ignore */ }
    };
    const onTime = () => {
      if (storageKey) {
        try { localStorage.setItem(storageKey, String(a.currentTime)); } catch { /* ignore */ }
      }
      if (typeof onTimeUpdate === "function") {
        try { onTimeUpdate(a); } catch { /* ignore */ }
      }
    };
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
    };
  }, [src, storageKey, initialSeek, onTimeUpdate]);

  if (!src) return null;
  return (
    <div className="w-full" data-testid={testid || "audio-player"}>
      {label && (
        <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-navy/60">
          <Play className="w-3 h-3 text-gold" strokeWidth={2} />
          {label}
        </div>
      )}
      <audio ref={audioRef} src={src} controls preload="metadata" className="w-full">
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}
